import React from "react";
import { Box, IconButton } from "@mui/joy";
import useMediaQuery from "@mui/material/useMediaQuery";
import { CloseIcon, SEdit, SSms } from "styles/StyledMUI";
import DarkTypography from "components/common/DarkTypography";
import IconButtonCTA from "components/common/IconButtonCTA";
import ExcursionPlanningSortieDrawerContent from "./ExcursionPlanningSortieDrawerContent";
import { ExcursionTemplateLimitsChips } from "../../../shared/excursionModal/display/ExcursionCountWithMaxChip";
import {
  buildExcursionTemplateLimitChipModel,
  excursionTimeRangeLabel,
} from "./excursionCardUtils";
import { colorTokens, excursionTooltipSublineColor, withAlpha } from "styles/globalStyles";
import { getExcursionStatusEmoji } from "../../shared/utils/planningUtils";
import type {
  BookingStatus,
  Excursion,
  ExcursionChipDropPending,
  ExcursionTemplateLimitsItem,
  GroupLabels,
} from "api/excursions/Excursion.typed";

const SORTIE_DRAWER_INSET_X = { xs: 1.25, sm: 2 } as const;

export type ExcursionPlanningGroupsDrawerProps = {
  open: boolean;
  excursion: Excursion | null;
  onClose: () => void;
  canEdit: boolean;
  getGroupLabels: (ex: Excursion) => GroupLabels[];
  makeGroupChipDropHandlers?: (targetExcursionId: number, targetGroupIndex: number) => object;
  onClientChipDragStart?: (sourceExcursionId: number) => void;
  onClientChipDragEnd?: () => void;
  onNavigateFullExcursionModal?: (e: React.MouseEvent, excursion: Excursion) => void;
  onOpenSms?: (excursion: Excursion) => void;
  /** When false, hides the header edit shortcut (e.g. prestations list drawer). Default true. */
  showExcursionEditButton?: boolean;
  showSmsButton?: boolean;
  excursionTemplatesList?: ExcursionTemplateLimitsItem[];
  bookingStatusMap?: Record<number, BookingStatus>;
  getCustomerInProgressTrainingsTooltip?: (customerId: number) => string | null;
  chipDropPending?: ExcursionChipDropPending | null;
};

export default function ExcursionPlanningGroupsDrawer({
  open,
  excursion,
  onClose,
  canEdit,
  getGroupLabels,
  makeGroupChipDropHandlers,
  onClientChipDragStart,
  onClientChipDragEnd,
  onNavigateFullExcursionModal,
  onOpenSms,
  showExcursionEditButton = true,
  showSmsButton = true,
  excursionTemplatesList,
  bookingStatusMap,
  getCustomerInProgressTrainingsTooltip,
  chipDropPending = null,
}: ExcursionPlanningGroupsDrawerProps) {
  const prefersReducedMotion = useMediaQuery("(prefers-reduced-motion: reduce)", {
    defaultMatches: false,
  });
  const [slideEntered, setSlideEntered] = React.useState(prefersReducedMotion);

  React.useLayoutEffect(() => {
    if (!excursion) {
      return;
    }

    if (prefersReducedMotion) {
      setSlideEntered(true);

      return;
    }

    setSlideEntered(false);

    const frame = requestAnimationFrame(() => {
      setSlideEntered(true);
    });

    return () => {
      cancelAnimationFrame(frame);
    };
  }, [excursion?.id, prefersReducedMotion]);

  if (!excursion) {
    return null;
  }

  const motionActive = Boolean(open) && !prefersReducedMotion;
  const timeStr = excursionTimeRangeLabel(excursion);
  const dateTitle = (() => {
    const isoDate = excursion.date?.slice(0, 10);
    if (!isoDate) {
      return "";
    }
    const [year, month, day] = isoDate.split("-");
    if (!year || !month || !day) {
      return isoDate;
    }
    return `${day}/${month}/${year}`;
  })();
  const limitChipsModel = buildExcursionTemplateLimitChipModel(excursion, excursionTemplatesList);

  return (
    <Box
      role="complementary"
      aria-hidden={!open}
      aria-label={`Sortie · ${excursion.name}, ${timeStr}`}
      sx={{
        flexShrink: 0,
        alignSelf: "stretch",
        display: open ? "flex" : "none",
        flexDirection: "column",
        minHeight: 0,
        width: { xs: "100%", sm: "min(400px, 36vw)", md: 380 },
        maxWidth: "100%",
        maxHeight: { xs: "min(52dvh, 520px)", sm: "none" },
        backgroundColor: colorTokens.bg.app,
        borderLeft: { xs: "none", sm: `1px solid ${colorTokens.border.default}` },
        borderTop: { xs: `1px solid ${colorTokens.border.default}`, sm: "none" },
        boxSizing: "border-box",
        transform: motionActive
          ? slideEntered
            ? "translate3d(0,0,0)"
            : { xs: "translate3d(0, 10px, 0)", sm: "translate3d(12px, 0, 0)" }
          : "none",
        transition: motionActive ? "transform 0.22s cubic-bezier(0.22, 1, 0.36, 1)" : "none",
        willChange: motionActive && !slideEntered ? "transform" : "auto",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: 1,
          px: SORTIE_DRAWER_INSET_X,
          py: 1.25,
          flexShrink: 0,
          borderBottom: `1px solid ${colorTokens.border.default}`,
          bgcolor: colorTokens.bg.surface,
        }}
      >
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <DarkTypography
            component="p"
            level="title-md"
            colorVariant="title"
            sx={{ fontWeight: 700, lineHeight: 1.25, wordBreak: "break-word" }}
          >
            {getExcursionStatusEmoji(excursion.status)} {excursion.name}
          </DarkTypography>
          <DarkTypography level="body-xs" sx={{ color: excursionTooltipSublineColor, mt: 0.35 }}>
            {dateTitle ? `${dateTitle} · ${timeStr}` : timeStr}
          </DarkTypography>
        </Box>
        <Box sx={{ display: "flex", flexShrink: 0, gap: 0.25, alignItems: "center" }}>
          {showSmsButton && onOpenSms ? (
            <IconButtonCTA
              size="sm"
              disabled={false}
              aria-label={`Envoyer un SMS pour ${excursion.name}`}
              onClick={() => onOpenSms(excursion)}
              sx={{
                minWidth: { xs: 44, sm: 36 },
                minHeight: { xs: 44, sm: 36 },
              }}
            >
              <SSms sx={{ fontSize: "1.125rem" }} />
            </IconButtonCTA>
          ) : null}
          {showExcursionEditButton ? (
            <IconButtonCTA
              size="sm"
              disabled={!canEdit}
              aria-label={
                canEdit
                  ? `Modifier la sortie ${excursion.name}`
                  : `Modification non disponible pour ${excursion.name}`
              }
              onClick={(event: React.MouseEvent) => {
                onNavigateFullExcursionModal?.(event, excursion);
              }}
              sx={{
                minWidth: { xs: 44, sm: 36 },
                minHeight: { xs: 44, sm: 36 },
              }}
            >
              <SEdit sx={{ fontSize: "1.125rem" }} />
            </IconButtonCTA>
          ) : null}
          <IconButton
            variant="plain"
            onClick={onClose}
            aria-label="Fermer le panneau"
            sx={{
              color: colorTokens.text.muted,
              "&:hover": { bgcolor: withAlpha(colorTokens.text.primary, 0.08) },
              minWidth: { xs: 44, sm: 36 },
              minHeight: { xs: 44, sm: 36 },
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </Box>
      <Box
        sx={{
          flex: 1,
          overflowY: "auto",
          overflowX: "hidden",
          p: SORTIE_DRAWER_INSET_X,
          WebkitOverflowScrolling: "touch",
          boxSizing: "border-box",
          minHeight: 0,
        }}
      >
        <ExcursionPlanningSortieDrawerContent
          excursion={excursion}
          canEdit={canEdit}
          groupLabels={getGroupLabels(excursion)}
          makeGroupChipDropHandlers={makeGroupChipDropHandlers}
          excursionTemplatesList={excursionTemplatesList}
          bookingStatusMap={bookingStatusMap}
          getCustomerInProgressTrainingsTooltip={getCustomerInProgressTrainingsTooltip}
          onClientDragStart={onClientChipDragStart}
          onClientDragEnd={onClientChipDragEnd}
          chipDropPending={chipDropPending?.excursionId === excursion.id ? chipDropPending : null}
        />
      </Box>
      <Box
        component="footer"
        sx={{
          flexShrink: 0,
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          justifyContent: "flex-end",
          gap: 0.75,
          py: 1,
          px: SORTIE_DRAWER_INSET_X,
          bgcolor: colorTokens.bg.surface,
        }}
      >
        <ExcursionTemplateLimitsChips {...limitChipsModel} size="sm" />
      </Box>
    </Box>
  );
}
